import { useState } from "react";

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      form.reset();
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/db8u0dhrh5tg6sock120", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (!response.ok || parsed?.code !== "OK" || /spam/i.test(serverMsg)) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form data-readdy-form id="about-feedback-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="fb-name" className="text-xs font-medium text-foreground-700">
            你的称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入称呼"
            className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-accent-500 focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="fb-email" className="text-xs font-medium text-foreground-700">
            联系邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-accent-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="fb-topic" className="text-xs font-medium text-foreground-700">
          反馈类型
        </label>
        <select
          id="fb-topic"
          name="topic"
          defaultValue="内容建议"
          className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-accent-500 focus:outline-none"
        >
          <option value="内容建议">内容建议</option>
          <option value="剧集信息纠错">剧集信息纠错</option>
          <option value="合作与投稿">合作与投稿</option>
          <option value="其他问题">其他问题</option>
        </select>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="fb-message" className="text-xs font-medium text-foreground-700">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="请描述你的建议或问题（最多 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-accent-500 focus:outline-none"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="fb-extra-field"
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-1 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-fill text-lg"></i>
            提交反馈
          </>
        )}
      </button>

      {status === "success" && (
        <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-fill text-lg"></i>
          感谢你的反馈，我们已经收到，会认真阅读每一条建议。
        </p>
      )}
      {status === "error" && (
        <p className="flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
          <i className="ri-error-warning-fill text-lg"></i>
          {formError}
        </p>
      )}
    </form>
  );
}